import { useState, useRef, forwardRef } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Lock, Loader2, Mail, ArrowRight, Send, ArrowDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.8, ease: "easeOut" }
};

const stagger = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

// ============ CINEMATIC IMAGES ============
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1762463176312-1757d5125c85?auto=format&fit=crop&w=1920&q=80",
  coins: "https://images.unsplash.com/photo-1624365169106-1f1f4cd65c91?auto=format&fit=crop&w=800&q=80",
  deal: "https://images.unsplash.com/photo-1638262052640-82e94d64664a?auto=format&fit=crop&w=800&q=80",
  stream: "https://images.unsplash.com/photo-1666858443985-fead1d59b4f7?auto=format&fit=crop&w=800&q=80",
  networking: "https://images.unsplash.com/photo-1781955781178-6ea29c66e860?auto=format&fit=crop&w=800&q=80",
  bars: "https://images.unsplash.com/photo-1762463176417-2c5e63767bd2?auto=format&fit=crop&w=800&q=80"
};

// ============ HERO SECTION ============
const HeroSection = ({ onApplyClick }) => (
  <section className="min-h-[100svh] flex flex-col items-center justify-center px-4 sm:px-6 relative overflow-hidden">
    {/* Cinematic Background Image */}
    <div className="absolute inset-0">
      <img 
        src={IMAGES.hero} 
        alt="" 
        className="w-full h-full object-cover opacity-30"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-void/80 via-void/70 to-void" />
      <div className="absolute inset-0 bg-gradient-to-r from-void/50 via-transparent to-void/50" />
    </div>
    
    <motion.div 
      className="text-center max-w-5xl mx-auto relative z-10 px-2"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.2 }}
    >
      <motion.p 
        className="font-manrope text-[9px] sm:text-[10px] md:text-[11px] tracking-[0.3em] sm:tracking-[0.4em] text-gold-500 uppercase mb-4 sm:mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        Precious Metals & Numismatics
      </motion.p>
      
      <motion.h1 
        className="font-cinzel text-5xl sm:text-7xl md:text-9xl lg:text-[12rem] tracking-wide mb-4 sm:mb-6 leading-none"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.8 }}
      >
        THE ROOM
      </motion.h1>

      <motion.div 
        className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 md:gap-6 text-stone-400 mb-6 sm:mb-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
      >
        <span className="font-manrope text-xs sm:text-sm md:text-base tracking-wider">LAS VEGAS</span>
        <span className="w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full bg-gold-500" />
        <span className="font-manrope text-xs sm:text-sm md:text-base tracking-wider">DECEMBER 11–12, 2026</span>
      </motion.div>

      <motion.div
        className="mb-8 sm:mb-12"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
      >
        <p className="font-cormorant text-xl sm:text-2xl md:text-4xl lg:text-5xl text-parchment italic leading-tight mb-2 sm:mb-4">
          The people who move the market.
        </p>
        <p className="font-cormorant text-xl sm:text-2xl md:text-4xl lg:text-5xl text-gold-500 italic leading-tight">
          In one room.
        </p>
      </motion.div>

      <motion.p 
        className="font-manrope text-xs sm:text-sm text-stone-500 mb-8 sm:mb-10 max-w-xl mx-auto px-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1 }}
      >
        200–300 curated attendees. Collectors, dealers, creators, founders, and the people shaping the future of the market.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3 }}
      >
        <button
          onClick={onApplyClick}
          data-testid="hero-request-access-btn"
          className="inline-flex items-center gap-2 sm:gap-3 px-8 sm:px-12 py-4 sm:py-5 bg-gold-500 text-charcoal font-manrope text-[10px] sm:text-xs tracking-[0.15em] sm:tracking-[0.2em] uppercase hover:bg-gold-400 transition-all active:scale-95"
        >
          Request Access
          <ArrowRight className="w-3 sm:w-4 h-3 sm:h-4" />
        </button>
      </motion.div>
    </motion.div>

    {/* Scroll indicator - hidden on mobile */}
    <motion.div 
      className="absolute bottom-6 sm:bottom-8 hidden sm:block"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 2 }}
    >
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        <ArrowDown className="w-5 h-5 text-stone-600" />
      </motion.div>
    </motion.div>
  </section>
);

// ============ THE VALUE SECTION ============
const ValueSection = () => (
  <section className="py-20 sm:py-24 md:py-32 lg:py-48 border-t border-stone-800 relative overflow-hidden">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <motion.div className="text-center" {...fadeUp}>
        <h2 className="font-cinzel text-2xl sm:text-3xl md:text-5xl lg:text-6xl text-parchment leading-tight mb-4 sm:mb-8">
          The value isn't what's on the schedule.
        </h2>
        <h2 className="font-cinzel text-2xl sm:text-3xl md:text-5xl lg:text-6xl text-gold-500 leading-tight mb-10 sm:mb-16">
          The value is who's in the room.
        </h2>
        
        <p className="font-manrope text-xs sm:text-sm md:text-base text-stone-400 max-w-3xl mx-auto leading-relaxed px-2">
          The Room exists to concentrate the people, inventory, audience, relationships and opportunities 
          that drive the collectibles market into one intimate environment.
        </p>
      </motion.div>

      <motion.div 
        className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 mt-12 sm:mt-20"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
      >
        {[
          "No massive convention floor.",
          "No thousands of random attendees.",
          "No traditional conference experience."
        ].map((text, i) => (
          <motion.p 
            key={i}
            className="font-manrope text-xs sm:text-sm text-stone-600 text-center py-2 sm:py-0"
            {...stagger}
            transition={{ delay: 0.4 + i * 0.1 }}
          >
            {text}
          </motion.p>
        ))}
      </motion.div>

      <motion.p 
        className="font-cormorant text-xl sm:text-2xl md:text-3xl text-parchment italic text-center mt-10 sm:mt-16"
        {...fadeUp}
      >
        Just the right people in the right room.
      </motion.p>
    </div>
  </section>
);

// ============ THE EXPERIENCE SECTION ============
const ExperienceSection = () => {
  const experiences = [
    { title: "DEAL", desc: "Private conversations, meetings, partnerships, acquisitions, inventory and opportunities.", image: IMAGES.deal },
    { title: "STREAM", desc: "Creators and sellers broadcasting live from dedicated environments inside The Room.", image: IMAGES.stream },
    { title: "BUY. SELL. DISCOVER.", desc: "Exceptional inventory and opportunities brought together by the people who actually own, source and move it.", image: IMAGES.bars },
    { title: "CONNECT", desc: "Collectors, sellers, distributors, creators, founders and brands who normally interact online—finally in the same physical room.", image: IMAGES.networking },
    { title: "BUILD", desc: "New partnerships, distribution relationships, collaborations and businesses begin here.", image: IMAGES.coins }
  ];

  return (
    <section className="py-16 sm:py-24 md:py-32 border-t border-stone-800 bg-gradient-to-b from-stone-900/30 to-void">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.p 
          className="font-manrope text-[9px] sm:text-[10px] tracking-[0.3em] text-stone-600 uppercase text-center mb-3 sm:mb-4"
          {...fadeUp}
        >
          Inside The Room
        </motion.p>
        <motion.h2 
          className="font-cinzel text-3xl sm:text-4xl md:text-6xl text-parchment text-center mb-12 sm:mb-20"
          {...fadeUp}
        >
          THE EXPERIENCE
        </motion.h2>

        <div className="space-y-12 sm:space-y-16 md:space-y-24">
          {experiences.map((exp, i) => (
            <motion.div 
              key={i}
              className={`flex flex-col ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-6 sm:gap-8 md:gap-16`}
              {...fadeUp}
              transition={{ delay: i * 0.1 }}
            >
              <div className="flex-1 text-center md:text-left px-2">
                <h3 className="font-cinzel text-xl sm:text-2xl md:text-4xl text-gold-500 mb-3 sm:mb-4">{exp.title}</h3>
                <p className="font-manrope text-xs sm:text-sm text-stone-400 leading-relaxed max-w-md mx-auto md:mx-0">{exp.desc}</p>
              </div>
              <div className="flex-1 h-40 sm:h-48 md:h-64 w-full relative overflow-hidden border border-stone-800">
                <img 
                  src={exp.image} 
                  alt={exp.title}
                  className="w-full h-full object-cover opacity-60 hover:opacity-80 transition-opacity duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-void/80 via-transparent to-transparent" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ LIVE FROM THE ROOM ============
const LiveSection = () => (
  <section className="py-20 sm:py-24 md:py-32 lg:py-48 border-t border-stone-800">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <motion.div className="text-center mb-12 sm:mb-20" {...fadeUp}>
        <p className="font-manrope text-[9px] sm:text-[10px] tracking-[0.3em] text-gold-500 uppercase mb-3 sm:mb-4">
          Live Commerce
        </p>
        <h2 className="font-cinzel text-3xl sm:text-4xl md:text-6xl text-parchment mb-4 sm:mb-6">
          LIVE FROM THE ROOM
        </h2>
        <p className="font-cormorant text-xl sm:text-2xl md:text-3xl text-stone-300 italic">
          The market will be watching.
        </p>
      </motion.div>

      <motion.p 
        className="font-manrope text-xs sm:text-sm text-stone-400 text-center max-w-2xl mx-auto mb-12 sm:mb-20 px-2"
        {...fadeUp}
      >
        Some of the industry's largest creators and sellers will broadcast live from inside The Room. 
        Major auctions. Rare inventory. Product launches. Reveals. Drops. Collaborations.
      </motion.p>

      <div className="grid sm:grid-cols-2 gap-4 sm:gap-8">
        <motion.div 
          className="border border-stone-800 p-6 sm:p-10 md:p-14"
          {...fadeUp}
        >
          <h3 className="font-cinzel text-xl sm:text-2xl text-parchment mb-3 sm:mb-4">THE STREAM FLOOR</h3>
          <p className="font-manrope text-xs sm:text-sm text-stone-500 leading-relaxed">
            Dedicated live-commerce environments where selected attendees and creators can broadcast from The Room.
          </p>
        </motion.div>

        <motion.div 
          className="border border-gold-500/30 bg-gold-500/5 p-6 sm:p-10 md:p-14"
          {...fadeUp}
          transition={{ delay: 0.1 }}
        >
          <h3 className="font-cinzel text-xl sm:text-2xl text-gold-500 mb-3 sm:mb-4">THE MAIN STAGE</h3>
          <p className="font-manrope text-xs sm:text-sm text-stone-400 leading-relaxed mb-4 sm:mb-6">
            A professionally produced headline broadcast environment featuring selected creators, companies, major auctions, launches and special programming.
          </p>
          <p className="font-manrope text-[9px] sm:text-[10px] text-stone-600 uppercase tracking-wider">
            Main Stage access reserved for selected creators and strategic partners
          </p>
        </motion.div>
      </div>
    </div>
  </section>
);

// ============ FRIDAY SECTION ============
const FridaySection = () => (
  <section className="py-20 sm:py-24 md:py-32 border-t border-stone-800 bg-gradient-to-b from-void to-stone-900/20">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <motion.div className="text-center mb-10 sm:mb-16" {...fadeUp}>
        <p className="font-manrope text-[9px] sm:text-[10px] tracking-[0.3em] text-stone-600 uppercase mb-3 sm:mb-4">
          Friday
        </p>
        <h2 className="font-cinzel text-4xl sm:text-5xl md:text-7xl text-parchment mb-4 sm:mb-8">
          THE ROOM
        </h2>
        <p className="font-cormorant text-lg sm:text-xl md:text-2xl text-stone-400 italic max-w-2xl mx-auto px-2">
          This is where the market comes together.
        </p>
      </motion.div>

      <motion.div 
        className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 md:gap-6 mb-10 sm:mb-16"
        {...fadeUp}
      >
        {["Private meetings", "Deals", "Inventory", "Live commerce", "Content", "Partnerships", "Product launches", "Relationships"].map((item, i) => (
          <div key={i} className="text-center py-2 sm:py-4">
            <p className="font-manrope text-[10px] sm:text-xs text-stone-500">{item}</p>
          </div>
        ))}
      </motion.div>

      <motion.div 
        className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3"
        {...fadeUp}
      >
        {[
          "THE MARKETPLACE",
          "PRIVATE DEAL ROOMS", 
          "THE STREAM FLOOR",
          "THE MAIN STAGE",
          "SOVEREIGN HOSPITALITY",
          "FOUNDING CIRCLE",
          "BRAND EXPERIENCES"
        ].map((item, i) => (
          <div 
            key={i} 
            className="border border-stone-800 p-3 sm:p-4 md:p-6 text-center hover:border-stone-700 transition-colors"
          >
            <p className="font-manrope text-[8px] sm:text-[10px] md:text-xs text-stone-400 tracking-wider">{item}</p>
          </div>
        ))}
      </motion.div>
    </div>
  </section>
);

// ============ FRIDAY NIGHT SECTION ============
const FridayNightSection = () => (
  <section className="py-20 sm:py-24 md:py-32 lg:py-48 border-t border-stone-800 relative overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-r from-void via-stone-900/50 to-void" />
    
    <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
      <motion.div {...fadeUp}>
        <p className="font-manrope text-[9px] sm:text-[10px] tracking-[0.3em] text-gold-500 uppercase mb-3 sm:mb-4">
          Friday Night
        </p>
        <h2 className="font-cinzel text-3xl sm:text-4xl md:text-6xl text-parchment mb-4 sm:mb-8">
          THE ROOM: AFTER DARK
        </h2>
        <p className="font-cormorant text-xl sm:text-2xl md:text-3xl text-stone-300 italic mb-8 sm:mb-12">
          When The Room closes, Vegas begins.
        </p>
        
        <div className="flex flex-wrap justify-center gap-4 sm:gap-6 md:gap-10 mb-8 sm:mb-12">
          {["Music", "Entertainment", "Hospitality", "Surprises"].map((item, i) => (
            <p key={i} className="font-manrope text-xs sm:text-sm text-stone-500">{item}</p>
          ))}
        </div>

        <p className="font-manrope text-[10px] sm:text-xs text-stone-600 max-w-md mx-auto">
          The right people without the business agenda.
        </p>
      </motion.div>
    </div>
  </section>
);

// ============ SATURDAY / ALL IN SECTION ============
const SaturdaySection = () => (
  <section className="py-20 sm:py-24 md:py-32 border-t border-stone-800 bg-gradient-to-b from-stone-900/30 to-void">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <motion.div className="text-center mb-12 sm:mb-20" {...fadeUp}>
        <p className="font-manrope text-[9px] sm:text-[10px] tracking-[0.3em] text-stone-600 uppercase mb-3 sm:mb-4">
          Saturday
        </p>
        <h2 className="font-cinzel text-4xl sm:text-5xl md:text-7xl text-parchment mb-3 sm:mb-4">
          THE ROOM: ALL IN
        </h2>
        <p className="font-cormorant text-lg sm:text-xl md:text-2xl text-stone-400 italic">
          Saturday changes the energy.
        </p>
      </motion.div>

      <motion.div className="grid sm:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-20" {...fadeUp}>
        <div className="px-2">
          <p className="font-manrope text-xs sm:text-sm text-stone-400 leading-relaxed mb-4 sm:mb-6">
            A premium poker experience for approximately 100 selected attendees. Collectors. Creators. Sellers. Founders. Industry personalities.
          </p>
          <p className="font-manrope text-xs sm:text-sm text-stone-500">
            The prize pool is built around extraordinary collectible assets—gold, silver, rare coins, cards, memorabilia, experiences and partner-contributed prizes.
          </p>
        </div>

        {/* The Sovereign Vault */}
        <div className="border border-gold-500/30 bg-gold-500/5 p-6 sm:p-10 text-center">
          <p className="font-manrope text-[9px] sm:text-[10px] tracking-[0.3em] text-gold-500 uppercase mb-3 sm:mb-4">
            The Prize Pool
          </p>
          <h3 className="font-cinzel text-2xl sm:text-3xl md:text-4xl text-parchment mb-2">
            THE SOVEREIGN VAULT
          </h3>
          <p className="font-manrope text-[10px] sm:text-xs text-stone-500 mt-3 sm:mt-4">
            Growing collection of extraordinary prizes contributed by Sovereign and its partners.
          </p>
        </div>
      </motion.div>
    </div>
  </section>
);

// ============ WHO'S IN THE ROOM ============
const WhoSection = () => {
  const types = [
    "COLLECTORS", "DEALERS", "CREATORS", "SELLERS", 
    "DISTRIBUTORS", "REPACKERS", "FOUNDERS", "BRANDS", 
    "INVESTORS", "MARKET MAKERS"
  ];

  return (
    <section className="py-20 sm:py-24 md:py-32 lg:py-48 border-t border-stone-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div className="text-center mb-10 sm:mb-16" {...fadeUp}>
          <p className="font-manrope text-[9px] sm:text-[10px] tracking-[0.3em] text-gold-500 uppercase mb-3 sm:mb-4">
            Curated for Impact
          </p>
          <h2 className="font-cinzel text-3xl sm:text-4xl md:text-6xl text-parchment">
            WHO'S IN THE ROOM
          </h2>
        </motion.div>

        <motion.div 
          className="flex flex-wrap justify-center gap-2 sm:gap-4 md:gap-6 mb-10 sm:mb-16"
          {...fadeUp}
        >
          {types.map((type, i) => (
            <motion.span 
              key={i}
              className="px-3 sm:px-6 py-2 sm:py-3 border border-stone-800 font-manrope text-[10px] sm:text-xs tracking-wider text-stone-400 hover:border-gold-500/30 hover:text-gold-500 transition-all cursor-default"
              {...stagger}
              transition={{ delay: i * 0.05 }}
            >
              {type}
            </motion.span>
          ))}
        </motion.div>

        <motion.div className="text-center max-w-3xl mx-auto px-2" {...fadeUp}>
          <p className="font-manrope text-xs sm:text-sm text-stone-400 leading-relaxed mb-4 sm:mb-6">
            Some attendees move millions of dollars in product. Some command enormous audiences. 
            Some control distribution. Some are serious collectors who want closer access to the people, 
            assets and opportunities shaping the market.
          </p>
          <p className="font-cormorant text-xl sm:text-2xl md:text-3xl text-parchment italic">
            They all take the market seriously.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// ============ ACCESS SECTION ============
const AccessSection = ({ onApplyClick, onNominateClick }) => (
  <section className="py-20 sm:py-24 md:py-32 border-t border-stone-800 bg-gradient-to-b from-stone-900/30 to-void">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
      <motion.div {...fadeUp}>
        <p className="font-manrope text-[9px] sm:text-[10px] tracking-[0.3em] text-gold-500 uppercase mb-3 sm:mb-4">
          Access is Limited
        </p>
        <h2 className="font-cinzel text-3xl sm:text-4xl md:text-5xl text-parchment mb-4 sm:mb-6">
          REQUEST ACCESS
        </h2>
        <p className="font-manrope text-xs sm:text-sm text-stone-500 mb-8 sm:mb-12 max-w-xl mx-auto px-2">
          Attendance is curated. Applications are reviewed. Approved applicants receive private access to purchase their ticket.
        </p>

        {/* Process - simplified on mobile */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 md:gap-8 mb-10 sm:mb-16">
          {["APPLY", "GET APPROVED", "RECEIVE INVITATION", "CHOOSE ACCESS", "ENTER"].map((step, i) => (
            <div key={i} className="flex items-center gap-2 sm:gap-4 md:gap-8">
              <span className="font-manrope text-[10px] sm:text-xs text-stone-500">{step}</span>
              {i < 4 && <span className="hidden sm:block text-stone-700">→</span>}
            </div>
          ))}
        </div>

        {/* Access Tiers Preview */}
        <div className="grid sm:grid-cols-3 gap-3 sm:gap-4 mb-10 sm:mb-16">
          <div className="border border-stone-800 p-4 sm:p-6 text-left">
            <p className="font-cinzel text-base sm:text-lg text-parchment mb-1 sm:mb-2">ROOM ACCESS</p>
            <p className="font-cormorant text-xl sm:text-2xl text-gold-500 mb-2 sm:mb-3">$1,000</p>
            <p className="font-manrope text-[10px] sm:text-xs text-stone-500">Full two-day experience</p>
          </div>
          <div className="border border-stone-700 p-4 sm:p-6 text-left">
            <p className="font-cinzel text-base sm:text-lg text-parchment mb-1 sm:mb-2">SOVEREIGN ACCESS</p>
            <p className="font-cormorant text-xl sm:text-2xl text-gold-500 mb-2 sm:mb-3">$2,500</p>
            <p className="font-manrope text-[10px] sm:text-xs text-stone-500">Elevated hospitality & priority</p>
          </div>
          <div className="border border-gold-500/30 bg-gold-500/5 p-4 sm:p-6 text-left">
            <p className="font-cinzel text-base sm:text-lg text-gold-500 mb-1 sm:mb-2">FOUNDING CIRCLE</p>
            <p className="font-cormorant text-xl sm:text-2xl text-gold-500 mb-2 sm:mb-3">$5,000</p>
            <p className="font-manrope text-[10px] sm:text-xs text-stone-400">Limited to ~50. Highest access.</p>
          </div>
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
          <button
            onClick={onApplyClick}
            data-testid="apply-access-btn"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-10 py-3 sm:py-4 bg-gold-500 text-charcoal font-manrope text-[10px] sm:text-xs tracking-[0.15em] uppercase hover:bg-gold-400 transition-all active:scale-95"
          >
            Apply to Enter The Room
            <ArrowRight className="w-3 sm:w-4 h-3 sm:h-4" />
          </button>
          <button
            onClick={onNominateClick}
            data-testid="nominate-btn"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-10 py-3 sm:py-4 border border-stone-600 text-stone-300 font-manrope text-[10px] sm:text-xs tracking-[0.15em] uppercase hover:border-gold-500 hover:text-gold-500 transition-all active:scale-95"
          >
            Nominate Someone
          </button>
        </div>
      </motion.div>
    </div>
  </section>
);

// ============ PARTNER SECTION ============
const PartnerSection = () => (
  <section className="py-20 sm:py-24 md:py-32 border-t border-stone-800">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
      <motion.div {...fadeUp}>
        <p className="font-manrope text-[9px] sm:text-[10px] tracking-[0.3em] text-stone-600 uppercase mb-3 sm:mb-4">
          Strategic Partnerships
        </p>
        <h2 className="font-cinzel text-3xl sm:text-4xl md:text-5xl text-parchment mb-4 sm:mb-6">
          OWN THE ROOM
        </h2>
        <p className="font-cormorant text-lg sm:text-xl md:text-2xl text-stone-400 italic mb-8 sm:mb-12">
          The most valuable partnerships aren't impressions. They're position.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-8 sm:mb-12">
          {[
            { name: "Presenting Partner", price: "$100K+" },
            { name: "Main Stage", price: "$50K+" },
            { name: "Sovereign Vault", price: "$50K+" },
            { name: "After Dark", price: "$35K+" },
            { name: "Stream Floor", price: "$25K+" },
            { name: "Deal Room", price: "$15K+" },
            { name: "Poker Table", price: "$10K+" },
            { name: "Custom", price: "Inquire" }
          ].map((item, i) => (
            <div key={i} className="border border-stone-800 p-3 sm:p-4 hover:border-stone-700 transition-colors">
              <p className="font-manrope text-[8px] sm:text-[10px] text-stone-500 uppercase tracking-wider mb-1">{item.name}</p>
              <p className="font-cormorant text-base sm:text-lg text-gold-500">{item.price}</p>
            </div>
          ))}
        </div>

        <Link
          to="/partners"
          className="inline-flex items-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 border border-stone-600 text-stone-300 font-manrope text-[10px] sm:text-xs tracking-[0.15em] uppercase hover:border-gold-500 hover:text-gold-500 transition-all active:scale-95"
        >
          Partner Inquiries
          <ArrowRight className="w-3 sm:w-4 h-3 sm:h-4" />
        </Link>
      </motion.div>
    </div>
  </section>
);

// ============ ACCESS LOOKUP ============
const AccessLookup = forwardRef((props, ref) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/access/lookup?email=${encodeURIComponent(email)}`);
      if (response.data.found) {
        navigate(`/access/${response.data.access_token}`);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section ref={ref} className="py-12 sm:py-16 border-t border-stone-800 bg-stone-900/30">
      <div className="max-w-xl mx-auto px-4 sm:px-6 text-center">
        <Lock className="w-4 sm:w-5 h-4 sm:h-5 text-stone-600 mx-auto mb-2 sm:mb-3" />
        <p className="font-manrope text-[10px] sm:text-xs text-stone-500 mb-4 sm:mb-6">Already approved? Enter your email</p>
        <form onSubmit={handleLookup} className="flex gap-2 sm:gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            data-testid="access-lookup-email"
            className="flex-1 bg-void border border-stone-700 px-3 sm:px-4 py-2.5 sm:py-3 font-manrope text-xs sm:text-sm text-parchment placeholder:text-stone-600 focus:outline-none focus:border-gold-500 transition-colors"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="access-lookup-btn"
            className="px-4 sm:px-6 py-2.5 sm:py-3 bg-stone-800 text-parchment font-manrope text-[10px] sm:text-xs tracking-wider uppercase hover:bg-gold-600 transition-colors disabled:opacity-50 active:scale-95"
          >
            {loading ? <Loader2 className="w-3 sm:w-4 h-3 sm:h-4 animate-spin" /> : "Go"}
          </button>
        </form>
      </div>
    </section>
  );
});

// ============ APPLICATION FORM ============
const ApplicationForm = forwardRef(({ onClose }, ref) => {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", primary_role: "", 
    primary_market: "", annual_volume: "", why_room: "", honeypot: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const roles = ["Buyer/Collector", "Dealer", "Distributor/Wholesaler", "Streamer/Creator", "Founder/Platform", "Investor", "Other"];
  const markets = ["Gold & Silver", "Coins", "Sports Cards", "Memorabilia", "Watches", "Other"];
  const volumes = ["Under $50k", "$50k - $250k", "$250k - $1M", "$1M - $5M", "$5M+"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.honeypot) return;
    setLoading(true);
    try {
      await axios.post(`${API}/invitation-request`, form);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div ref={ref} className="text-center py-12">
        <Send className="w-8 h-8 text-gold-500 mx-auto mb-6" />
        <h3 className="font-cinzel text-2xl text-parchment mb-3">Application Received</h3>
        <p className="font-manrope text-sm text-stone-400 mb-8">We review every application personally. If it's a fit, we'll be in touch.</p>
        <button onClick={onClose} className="font-manrope text-xs text-gold-500 hover:text-gold-400">Close</button>
      </div>
    );
  }

  return (
    <form ref={ref} onSubmit={handleSubmit} className="space-y-5">
      <input type="text" name="hp" value={form.honeypot} onChange={(e) => setForm({...form, honeypot: e.target.value})} className="hidden" />
      
      <div className="grid md:grid-cols-2 gap-5">
        <input type="text" required placeholder="Full Name *" value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} className="w-full bg-transparent border border-stone-700 px-4 py-3 font-manrope text-sm text-parchment placeholder:text-stone-600 focus:border-gold-500 focus:outline-none" />
        <input type="email" required placeholder="Email *" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full bg-transparent border border-stone-700 px-4 py-3 font-manrope text-sm text-parchment placeholder:text-stone-600 focus:border-gold-500 focus:outline-none" />
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <select required value={form.primary_role} onChange={(e) => setForm({...form, primary_role: e.target.value})} className="w-full bg-void border border-stone-700 px-4 py-3 font-manrope text-sm text-parchment focus:border-gold-500 focus:outline-none">
          <option value="">Primary Role *</option>
          {roles.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select required value={form.primary_market} onChange={(e) => setForm({...form, primary_market: e.target.value})} className="w-full bg-void border border-stone-700 px-4 py-3 font-manrope text-sm text-parchment focus:border-gold-500 focus:outline-none">
          <option value="">Primary Market *</option>
          {markets.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      <select required value={form.annual_volume} onChange={(e) => setForm({...form, annual_volume: e.target.value})} className="w-full bg-void border border-stone-700 px-4 py-3 font-manrope text-sm text-parchment focus:border-gold-500 focus:outline-none">
        <option value="">Annual Volume *</option>
        {volumes.map(v => <option key={v} value={v}>{v}</option>)}
      </select>
      <textarea required maxLength={300} rows={3} placeholder="Why do you want to be in The Room? *" value={form.why_room} onChange={(e) => setForm({...form, why_room: e.target.value})} className="w-full bg-transparent border border-stone-700 px-4 py-3 font-manrope text-sm text-parchment placeholder:text-stone-600 focus:border-gold-500 focus:outline-none resize-none" />
      {error && <p className="text-red-400 font-manrope text-sm">{error}</p>}
      <button type="submit" disabled={loading} className="w-full py-4 bg-gold-500 text-charcoal font-manrope text-xs tracking-[0.15em] uppercase hover:bg-gold-400 disabled:opacity-50 flex items-center justify-center gap-2">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Application"}
      </button>
    </form>
  );
});

// ============ NOMINATION FORM ============
const NominationForm = forwardRef(({ onClose }, ref) => {
  const [form, setForm] = useState({
    nominator_name: "", nominator_email: "", nominee_name: "", 
    nominee_email: "", nominee_role: "", relationship: "", why_nominee: "", honeypot: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const roles = ["Buyer/Collector", "Dealer", "Distributor/Wholesaler", "Streamer/Creator", "Founder/Platform", "Investor", "Other"];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.honeypot) return;
    setLoading(true);
    try {
      await axios.post(`${API}/nomination-request`, form);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div ref={ref} className="text-center py-12">
        <Send className="w-8 h-8 text-gold-500 mx-auto mb-6" />
        <h3 className="font-cinzel text-2xl text-parchment mb-3">Nomination Received</h3>
        <p className="font-manrope text-sm text-stone-400 mb-8">We'll review and reach out if there's a fit.</p>
        <button onClick={onClose} className="font-manrope text-xs text-gold-500 hover:text-gold-400">Close</button>
      </div>
    );
  }

  return (
    <form ref={ref} onSubmit={handleSubmit} className="space-y-5">
      <input type="text" name="hp" value={form.honeypot} onChange={(e) => setForm({...form, honeypot: e.target.value})} className="hidden" />
      <div className="grid md:grid-cols-2 gap-5">
        <input type="text" required placeholder="Your Name *" value={form.nominator_name} onChange={(e) => setForm({...form, nominator_name: e.target.value})} className="w-full bg-transparent border border-stone-700 px-4 py-3 font-manrope text-sm text-parchment placeholder:text-stone-600 focus:border-gold-500 focus:outline-none" />
        <input type="email" required placeholder="Your Email *" value={form.nominator_email} onChange={(e) => setForm({...form, nominator_email: e.target.value})} className="w-full bg-transparent border border-stone-700 px-4 py-3 font-manrope text-sm text-parchment placeholder:text-stone-600 focus:border-gold-500 focus:outline-none" />
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <input type="text" required placeholder="Nominee's Name *" value={form.nominee_name} onChange={(e) => setForm({...form, nominee_name: e.target.value})} className="w-full bg-transparent border border-stone-700 px-4 py-3 font-manrope text-sm text-parchment placeholder:text-stone-600 focus:border-gold-500 focus:outline-none" />
        <input type="email" required placeholder="Nominee's Email *" value={form.nominee_email} onChange={(e) => setForm({...form, nominee_email: e.target.value})} className="w-full bg-transparent border border-stone-700 px-4 py-3 font-manrope text-sm text-parchment placeholder:text-stone-600 focus:border-gold-500 focus:outline-none" />
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <select required value={form.nominee_role} onChange={(e) => setForm({...form, nominee_role: e.target.value})} className="w-full bg-void border border-stone-700 px-4 py-3 font-manrope text-sm text-parchment focus:border-gold-500 focus:outline-none">
          <option value="">Nominee's Role *</option>
          {roles.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <input type="text" required placeholder="Your Relationship *" value={form.relationship} onChange={(e) => setForm({...form, relationship: e.target.value})} className="w-full bg-transparent border border-stone-700 px-4 py-3 font-manrope text-sm text-parchment placeholder:text-stone-600 focus:border-gold-500 focus:outline-none" />
      </div>
      <textarea required maxLength={300} rows={3} placeholder="Why should they be in The Room? *" value={form.why_nominee} onChange={(e) => setForm({...form, why_nominee: e.target.value})} className="w-full bg-transparent border border-stone-700 px-4 py-3 font-manrope text-sm text-parchment placeholder:text-stone-600 focus:border-gold-500 focus:outline-none resize-none" />
      {error && <p className="text-red-400 font-manrope text-sm">{error}</p>}
      <button type="submit" disabled={loading} className="w-full py-4 bg-gold-500 text-charcoal font-manrope text-xs tracking-[0.15em] uppercase hover:bg-gold-400 disabled:opacity-50 flex items-center justify-center gap-2">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Nomination"}
      </button>
    </form>
  );
});

// ============ FORM MODAL ============
const FormModal = ({ isOpen, onClose, activeTab, setActiveTab }) => {
  if (!isOpen) return null;

  return (
    <motion.div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-void/95 backdrop-blur-sm" onClick={onClose} />
      <motion.div 
        className="relative w-full max-w-lg bg-void border border-stone-800 p-8 md:p-10 max-h-[90vh] overflow-y-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <button onClick={onClose} className="absolute top-4 right-4 font-manrope text-stone-500 hover:text-parchment">✕</button>
        
        <div className="text-center mb-8">
          <h3 className="font-cinzel text-2xl text-parchment mb-2">
            {activeTab === "apply" ? "Apply to Enter The Room" : "Nominate Someone"}
          </h3>
          <p className="font-manrope text-xs text-stone-500 mt-2">
            {activeTab === "apply" ? "Applications are reviewed. Approved applicants receive private access." : "Know someone who should be in The Room?"}
          </p>
        </div>

        <div className="flex border-b border-stone-800 mb-8">
          <button onClick={() => setActiveTab("apply")} className={`flex-1 py-3 font-manrope text-xs tracking-wider uppercase ${activeTab === "apply" ? "text-gold-500 border-b-2 border-gold-500" : "text-stone-500"}`}>
            Apply
          </button>
          <button onClick={() => setActiveTab("nominate")} className={`flex-1 py-3 font-manrope text-xs tracking-wider uppercase ${activeTab === "nominate" ? "text-gold-500 border-b-2 border-gold-500" : "text-stone-500"}`}>
            Nominate
          </button>
        </div>

        {activeTab === "apply" ? <ApplicationForm onClose={onClose} /> : <NominationForm onClose={onClose} />}
      </motion.div>
    </motion.div>
  );
};

// ============ MAIN PAGE ============
const LandingPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("apply");
  const accessRef = useRef(null);

  const openApplyModal = () => { setActiveTab("apply"); setModalOpen(true); };
  const openNominateModal = () => { setActiveTab("nominate"); setModalOpen(true); };

  return (
    <main className="bg-void min-h-screen text-parchment">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 px-6 md:px-10 py-6 bg-gradient-to-b from-void via-void/80 to-transparent">
        <div className="flex justify-between items-center max-w-7xl mx-auto">
          <Link to="/" className="flex items-center gap-2 font-manrope text-[10px] tracking-[0.15em] text-stone-500 hover:text-gold-500 transition-colors uppercase">
            <ArrowLeft className="w-3 h-3" />
            Sovereign
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/partners" className="hidden md:block font-manrope text-[10px] tracking-[0.15em] text-stone-500 hover:text-gold-500 transition-colors uppercase">
              Partners
            </Link>
            <button
              onClick={() => accessRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
              className="flex items-center gap-2 px-4 py-2 border border-gold-500/40 text-gold-500 hover:bg-gold-500 hover:text-charcoal font-manrope text-[10px] tracking-[0.15em] uppercase transition-all"
            >
              <Lock className="w-3 h-3" />
              Access
            </button>
          </div>
        </div>
      </nav>

      {/* Page Sections */}
      <HeroSection onApplyClick={openApplyModal} />
      <ValueSection />
      <ExperienceSection />
      <LiveSection />
      <FridaySection />
      <FridayNightSection />
      <SaturdaySection />
      <WhoSection />
      <AccessSection onApplyClick={openApplyModal} onNominateClick={openNominateModal} />
      <PartnerSection />
      <AccessLookup ref={accessRef} />

      {/* Footer */}
      <footer className="py-12 border-t border-stone-800">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/" className="font-cinzel text-sm text-stone-600 tracking-wider">Sovereign</Link>
          <div className="flex items-center gap-8">
            <Link to="/partners" className="font-manrope text-[10px] text-stone-600 hover:text-parchment uppercase tracking-wider">Partners</Link>
            <a href="mailto:info@sovereigngroup.io" className="font-manrope text-[10px] text-stone-600 hover:text-parchment uppercase tracking-wider">Contact</a>
          </div>
        </div>
      </footer>

      {/* Form Modal */}
      <FormModal isOpen={modalOpen} onClose={() => setModalOpen(false)} activeTab={activeTab} setActiveTab={setActiveTab} />
    </main>
  );
};

export default LandingPage;
